import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { WidthProvider, Responsive } from 'react-grid-layout'

import type { ViewProps } from '../common-props'
import type { ReactElement } from 'react'
import type { Layouts, Layout as LayoutType } from 'react-grid-layout'
import type { IBuilderConfig, IDataBuilderConfig, IGraphBuilderConfig } from '~/types/graph-builder'

import { viewClassName } from '../common-props'

import mousePointer from '~/assets/mouse-pointer.svg'
import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import DynamicDataBlock from '~/components/DynamicDataBlock'
import DynamicGraphBlock from '~/components/DynamicGraphBlock'
import FavoriteStar from '~/components/FavoriteStar'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import { COMMON_GRID_PROPS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useChartConfigsListQuery } from '~/queries/graphs-builder'
import { usersKeys, useUpdateUserPreferencesMutation, useUserPreferencesQuery } from '~/queries/users'
import { formatConfig } from '~/utils/graphs-builder'
import { generateLayout, getDefaultLayout } from '~/utils/personal-dashboard'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ResponsiveGridLayout = WidthProvider(Responsive)

const CustomDashboard = ({ analyticsApiFn, eventsData, isAppLoading, kpiField }: ViewProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { appKey, deviceType } = useAppSummary()

  const { state } = useAnalyticsContext()

  const layoutClassNames = classNames(viewClassName, { 'filters-collapsed': state.isFiltersCollapsed })

  const [isFirstRender, setIsFirstRender] = useState(true)

  /** Queries & Mutations */

  const { isPending, mutateAsync: updateUserPreferencesMutation } = useUpdateUserPreferencesMutation()

  const { data: userPreferences, isLoading: isUserPreferencesLoading } = useUserPreferencesQuery()

  const favouriteGraphs = userPreferences?.favourite_graphs

  const { data: chartConfigs, isLoading: isChartConfigsListLoading } = useChartConfigsListQuery({
    appKey,
    deviceType,
  })

  const formattedConfigs = useMemo(() => {
    if (favouriteGraphs && favouriteGraphs.length) {
      const filterConfigs =
        chartConfigs?.data?.filter(({ graph_config_id }) => favouriteGraphs.find(({ id }) => id === graph_config_id)) ||
        []

      return formatConfig(filterConfigs) as unknown as IGraphBuilderConfig[]
    }

    return []
  }, [chartConfigs?.data, favouriteGraphs])

  const layouts = useMemo(() => {
    return userPreferences?.dashboard_layout?.custom_dashboard?.[0].layout
      ? generateLayout(userPreferences.dashboard_layout.custom_dashboard[0].layout, formattedConfigs)
      : getDefaultLayout(formattedConfigs)
  }, [formattedConfigs, userPreferences])

  /** Effects */

  useDocumentTitle('CA Custom Dashboard')

  /** Handlers */

  const handleLayoutChange = useCallback(
    async (_layout: LayoutType[], layouts: Layouts) => {
      if (userPreferences && !isFirstRender) {
        try {
          const idxToKeep = _layout.map(({ i }) => i)
          const layoutLg = layouts.lg?.filter(({ i }) => idxToKeep.includes(i)) || []
          const layoutMd = layouts.md?.filter(({ i }) => idxToKeep.includes(i)) || []

          const data = {
            custom_dashboard: [
              {
                layout: { lg: layoutLg, md: layoutMd },
                subtitle: '',
                title: '',
              },
            ],
          }

          await updateUserPreferencesMutation({ ...userPreferences, dashboard_layout: data })
          await queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })
        } catch (e) {
          displayToast('Layout change could not be saved to your preferences', 'info')
          captureException(e)
        }
      }

      if (isFirstRender) {
        setIsFirstRender(false)
      }
    },
    [isFirstRender, queryClient, updateUserPreferencesMutation, userPreferences]
  )

  const graphOptions = useCallback(
    <T extends IBuilderConfig>(config: T) => ({
      apiSelectionFn: analyticsApiFn,
      appKey,
      config,
      deviceType,
      eventsData,
      isAppLoading,
      kpiField,
    }),
    [analyticsApiFn, appKey, deviceType, eventsData, isAppLoading, kpiField]
  )

  const renderDataByGraphType = useCallback(
    (config: IGraphBuilderConfig) => {
      return config.series?.length === 1 && config.series[0].graphType === 'raw' ? (
        <DynamicDataBlock {...graphOptions(config as IDataBuilderConfig)} />
      ) : (
        <DynamicGraphBlock {...graphOptions(config as IGraphBuilderConfig)} />
      )
    },
    [graphOptions]
  )

  const handleResetLayout = async () => {
    if (userPreferences) {
      await updateUserPreferencesMutation({
        ...userPreferences,
        dashboard_layout: undefined,
      })

      await queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })
      displayToast('Layout reset successfully', 'success')
    }
  }

  /** Render */

  return (
    <>
      <Layout className={layoutClassNames} withMargin>
        <ContentLayout loading={isAppLoading || isChartConfigsListLoading || isUserPreferencesLoading}>
          <Button disabled={isPending} onClick={handleResetLayout}>
            Reset Layout
          </Button>
          <br />
          {layouts && formattedConfigs && formattedConfigs.length > 0 ? (
            <Section className="section">
              <ResponsiveGridLayout
                {...COMMON_GRID_PROPS}
                isBounded
                isDraggable
                layouts={layouts}
                onLayoutChange={handleLayoutChange}
                // NOTE: 75.5 is the row height for the grid, not the same as the other analytics pages for resize purposes
                rowHeight={75.5}
              >
                {formattedConfigs.map((config) => (
                  <div key={`${config.id}`}>{renderDataByGraphType(config)}</div>
                ))}
              </ResponsiveGridLayout>
            </Section>
          ) : (
            <div className="empty-favourites-view">
              <span className="empty-favourites-view__prompt">
                Bookmark your <span className="empty-favourites-view__prompt--emphasized">favourite</span> data.
              </span>
              <div className="empty-favourites-view__card">
                <FavoriteStar isActive />
                <object className="mouse-pointer" data={mousePointer} type="image/svg+xml" />
              </div>
            </div>
          )}
        </ContentLayout>
      </Layout>
    </>
  )
}

export default CustomDashboard
